import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Send, Paperclip, Zap, Bot, Loader2, Square, BookTemplate,
  ChevronUp, FileSpreadsheet, X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator, DropdownMenuLabel
} from '@/components/ui/dropdown-menu';

const QUICK_ACTIONS = [
  { label: 'Profile Dataset', text: 'Profile this dataset', emoji: '📊' },
  { label: 'Run NCA', text: 'Run NCA analysis on this dataset', emoji: '🧪' },
  { label: 'Run QC Checks', text: 'Run quality control checks on my analysis', emoji: '✅' },
  { label: 'Generate Report', text: 'Generate a regulatory report section', emoji: '📝' },
  { label: 'Simulate Doses', text: 'Run Monte Carlo simulations for dose optimization', emoji: '🎯' },
  { label: 'Regulatory Guidance', text: 'What are the regulatory requirements for this analysis?', emoji: '📋' },
];

const TEMPLATES = [
  { key: 'nca-single-dose', label: 'Single-Dose NCA', cat: 'NCA', steps: 4 },
  { key: 'nca-bioequivalence', label: 'Bioequivalence', cat: 'NCA', steps: 5 },
  { key: 'nca-dose-prop', label: 'Dose Proportionality', cat: 'NCA', steps: 4 },
  { key: 'popk-standard', label: 'Standard PopPK', cat: 'PopPK', steps: 8 },
  { key: 'popk-covariate', label: 'Covariate Analysis', cat: 'PopPK', steps: 5 },
  { key: 'pkpd-emax', label: 'Emax PKPD', cat: 'PKPD', steps: 4 },
  { key: 'er-efficacy-binary', label: 'Efficacy E-R', cat: 'E-R', steps: 4 },
  { key: 'er-safety', label: 'Safety E-R', cat: 'E-R', steps: 4 },
  { key: 'ddi-static', label: 'DDI Risk Assessment', cat: 'DDI', steps: 3 },
  { key: 'sim-dose-selection', label: 'Dose Selection Sim', cat: 'Sim', steps: 3 },
  { key: 'sim-target-attainment', label: 'Target Attainment', cat: 'Sim', steps: 3 },
  { key: 'sp-pediatric', label: 'Pediatric PK', cat: 'Pop', steps: 4 },
  { key: 'sp-renal', label: 'Renal Impairment', cat: 'Pop', steps: 4 },
  { key: 'rpt-fda-nca', label: 'FDA NCA Report', cat: 'Rpt', steps: 3 },
  { key: 'rpt-fda-poppk', label: 'FDA PopPK Report', cat: 'Rpt', steps: 3 },
  { key: 'sd-fih', label: 'FIH Study Design', cat: 'Design', steps: 3 },
];

const MODELS = [
  { id: 'sonnet', label: 'Claude Sonnet 4', desc: 'Fast, general purpose', badge: 'Default' },
  { id: 'opus', label: 'Claude Opus', desc: 'Thorough, complex analysis', badge: 'Pro' },
];

export function ChatComposer({ onSend, onFileUpload, onLaunchTemplate, isLoading = false, disabled = false, streamingAgent = null }) {
  const [message, setMessage] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [selectedModel, setSelectedModel] = useState('sonnet');
  const textareaRef = useRef(null);

  const handleSend = () => {
    if (!message.trim() || isLoading) return;
    onSend(message.trim());
    setMessage('');
    setUploadedFile(null);
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const handleInput = (e) => {
    setMessage(e.target.value);
    const el = textareaRef.current;
    if (el) { el.style.height = 'auto'; el.style.height = Math.min(el.scrollHeight, 160) + 'px'; }
  };

  return (
    <div className="px-2 sm:px-4 pb-3 sm:pb-4 pt-2">
      {/* Streaming indicator */}
      <AnimatePresence>
        {isLoading && streamingAgent && (
          <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="flex items-center gap-2 mb-2 px-1"
          >
            <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
            <span className="text-xs text-muted-foreground">{streamingAgent} is analyzing</span>
            <span className="text-xs text-muted-foreground animate-pulse">...</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* File chip */}
      <AnimatePresence>
        {uploadedFile && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-2"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted/50 text-xs">
              <FileSpreadsheet className="w-3.5 h-3.5 text-primary" />
              <span className="font-mono">{uploadedFile.name}</span>
              <button onClick={() => setUploadedFile(null)} className="hover:text-foreground"><X className="w-3 h-3" /></button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main input container */}
      <div className="relative rounded-2xl border border-border bg-background/60 backdrop-blur-md focus-within:ring-2 focus-within:ring-primary/30 transition-shadow duration-200">
        <div className="flex items-end gap-1.5 p-2.5">
          {/* File upload */}
          <Button
            variant="ghost" size="icon"
            onClick={onFileUpload}
            data-testid="chat-composer-attach-button"
            className="h-9 w-9 rounded-xl hover:bg-muted/50 flex-shrink-0 mb-0.5"
            disabled={disabled || isLoading}
          >
            <Paperclip className="w-4 h-4" />
          </Button>

          {/* Textarea */}
          <textarea
            ref={textareaRef}
            value={message}
            onChange={handleInput}
            onKeyDown={handleKeyDown}
            placeholder={isLoading ? 'Waiting for response...' : 'Describe your analysis or ask a question...'}
            data-testid="chat-composer-textarea"
            disabled={isLoading || disabled}
            rows={1}
            className="flex-1 resize-none bg-transparent border-0 outline-none text-sm placeholder:text-muted-foreground/50 max-h-[160px] py-2 focus:ring-0"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          />

          {/* Quick actions / Templates */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="chat-composer-analysis-type-menu"
                className="h-9 w-9 rounded-xl hover:bg-muted/50 flex-shrink-0 mb-0.5" disabled={disabled || isLoading}>
                <Zap className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel className="text-[10px] text-muted-foreground uppercase tracking-wider">Quick Actions</DropdownMenuLabel>
              {QUICK_ACTIONS.map((a, i) => (
                <DropdownMenuItem key={i} onClick={() => { setMessage(a.text); textareaRef.current?.focus(); }}>
                  <span className="mr-2">{a.emoji}</span> {a.label}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-[10px] text-muted-foreground uppercase tracking-wider">Workflow Templates</DropdownMenuLabel>
              {TEMPLATES.map((t) => (
                <DropdownMenuItem key={t.key} onClick={() => onLaunchTemplate?.(t.key)}>
                  <BookTemplate className="w-3.5 h-3.5 mr-2 text-primary" />
                  <span className="flex-1">{t.label}</span>
                  <span className="text-[9px] font-mono text-muted-foreground ml-2">{t.steps}s</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Model picker */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon"
                className="h-9 w-9 rounded-xl hover:bg-muted/50 flex-shrink-0 mb-0.5" disabled={disabled || isLoading}>
                <Bot className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuLabel className="text-[10px] text-muted-foreground uppercase tracking-wider">AI Model</DropdownMenuLabel>
              {MODELS.map(m => (
                <DropdownMenuItem key={m.id} onClick={() => setSelectedModel(m.id)} className={selectedModel === m.id ? 'bg-primary/10' : ''}>
                  <div className="flex-1">
                    <p className="text-sm font-medium">{m.label}</p>
                    <p className="text-[10px] text-muted-foreground">{m.desc}</p>
                  </div>
                  {selectedModel === m.id && <span className="text-[9px] font-mono text-primary">Active</span>}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Send / Stop */}
          {isLoading ? (
            <Button
              size="icon"
              className="h-9 w-9 rounded-xl bg-destructive/80 text-white hover:bg-destructive flex-shrink-0 mb-0.5"
              onClick={() => {}}
            >
              <Square className="w-3.5 h-3.5" />
            </Button>
          ) : (
            <Button
              onClick={handleSend}
              disabled={!message.trim() || disabled}
              data-testid="chat-composer-send-button"
              className="h-9 w-9 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 active:scale-[0.97] transition-transform flex-shrink-0 mb-0.5"
              size="icon"
            >
              <Send className="w-4 h-4" />
            </Button>
          )}
        </div>

        {/* Character count */}
        {message.length > 500 && (
          <div className="absolute bottom-1 right-14 text-[9px] text-muted-foreground/40 font-mono">
            {message.length}
          </div>
        )}
      </div>

      <p className="text-[10px] text-muted-foreground/40 text-center mt-2">
        PharmAgent may produce inaccurate results. Always verify with domain expertise.
      </p>
    </div>
  );
}
